import {
  ROOT_PITCH_CLASS,
  pitchClassToName,
} from "@/lib/music/theory-helpers";
import type { PieceTonality } from "./piece-overview-data";

/**
 * Generates a "didactic default" chord progression when the analyzer
 * didn't return a `signatureProgression`. We pick the perfect cadence
 * I–IV–V–I in major, i–iv–V–i in minor, universally recognizable as
 * "the harmony of this key" and short enough to fit the inline strip.
 *
 * Returns chord symbols spelled in the same accidental flavor as the
 * tonic (e.g. F major → ["F", "B♭", "C", "F"]). Returns an empty array
 * if the tonic isn't a recognized pitch, caller decides how to fall
 * back further (typically: render nothing).
 */

type Degree = readonly [semitones: number, suffix: "" | "m"];

const MAJOR_CADENCE: ReadonlyArray<Degree> = [
  [0, ""],
  [5, ""],
  [7, ""],
  [0, ""],
];

const MINOR_CADENCE: ReadonlyArray<Degree> = [
  [0, "m"],
  [5, "m"],
  [7, ""], // V majorisé (cadence parfaite mineure harmonique)
  [0, "m"],
];

export function defaultProgression(tonality: PieceTonality): string[] {
  const tonicPc = ROOT_PITCH_CLASS[tonality.tonic];

  if (tonicPc === undefined) {
    return [];
  }

  const cadence =
    tonality.mode === "major" ? MAJOR_CADENCE : MINOR_CADENCE;

  return cadence.map(([semitones, suffix]) => {
    const pc = (tonicPc + semitones) % 12;
    const root = pitchClassToName(pc, tonality.tonic);

    return `${root}${suffix}`;
  });
}
